import React from 'react'
import { Box, Container, Typography, Grid, Card, CardContent } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined'

const ModernHiringTools = () => {
  const theme = useTheme()

  const tools = [
    {
      id: 'hire-confidence',
      title: 'Hire with Confidence.',
      description: 'Target high-quality, verified candidates who match your clinical needs and are ready to take the next step.',
      icon: <LightbulbOutlinedIcon />
    },
    {
      id: 'cut-costs',
      title: 'Cut the Costs—Keep the Talent.',
      description: 'Skip third-party recruiters and fill positions quickly with direct access to qualified providers.',
      icon: <LightbulbOutlinedIcon />
    },
    {
      id: 'right-fit',
      title: 'Find the Right Fit, Fast.',
      description: 'Take command of how find clinicians with surgical precision: target your open roles by provider type, specialty, location and many other filters to ensure yours listings reach the right candidates.',
      icon: <LightbulbOutlinedIcon />
    },
    {
      id: 'streamline-process',
      title: 'Streamline Your Hiring Process.',
      description: 'Manage job postings, review candidate interest, and connect directly—all in one intuitive platform, built by physicians themselves.',
      icon: <LightbulbOutlinedIcon />
    }
  ]

  return (
    <Box sx={{ py: { xs: 6, sm: 10 }, backgroundColor: theme.palette.grey[50] }}>
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 6, sm: 8 } }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              color: theme.palette.grey[900],
              fontSize: { xs: '2rem', md: '2.5rem' },
              lineHeight: 1.2,
              mb: 3
            }}
          >
            Healthcare Recruitment Made Simple
          </Typography>
          <Typography
            variant="h3"
            sx={{
              color: theme.palette.grey[900],
              fontSize: '1.2rem',
              fontWeight: 400,
              lineHeight: 1.6,
              maxWidth: '1000px',
              mx: 'auto'
            }}
          >
            Connect directly with qualified clinicians.
          </Typography>
        </Box>

        {/* Cards Grid */}
        <Grid container spacing={4}>
          {tools.map((tool) => (
            <Grid item xs={12} sm={6} md={3} key={tool.id}>
              <Card
                sx={{
                  height: '100%',
                  backgroundColor: 'white',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '16px',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 30px rgba(0,0,0,0.1)',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      backgroundColor: theme.palette.primary.main,
                      zIndex: 1
                    }
                  }
                }}
              >
                <CardContent sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  {/* Icon */}
                  <Box sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        backgroundColor: theme.palette.primary.light,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: theme.palette.primary.main
                      }}
                    >
                      {tool.icon}
                    </Box>
                  </Box>

                  {/* Title */}
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.grey[900],
                      mb: 2,
                      fontSize: '1.25rem',
                      lineHeight: 1.3
                    }}
                  >
                    {tool.title}
                  </Typography>

                  {/* Description */}
                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.grey[900],
                      fontSize: '0.95rem',
                      lineHeight: 1.6,
                      opacity: 0.8
                    }}
                  >
                    {tool.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  )
}

export default ModernHiringTools
